import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import VuiTypography from "components/VuiTypography";
import colors from "assets/theme/base/colors";
import linearGradient from "assets/theme/functions/linearGradient";
import { useCompany } from "context/CompanyContext";
import { useClient } from "context/ClientContext";
import { useAuthz } from "context/AuthzContext";
import { listClientProfiles, listAllDirectorsForTenant, listIndividualClients } from "services/tenantUserAdminService";

const { gradients, borderCol, white, info } = colors;

const HIDDEN_RAIL_ROUTES = new Set(["/health-check", "/issues-fixes", "/integrations", "/legal", "/organisations", "/tenant-users", "/role-assignment", "/notifications", "/email-config", "/settings"]);
const MODE_STORAGE_KEY = "ledgerhub-left-rail-mode";
const MODE_OPTIONS = [
  { value: "companies", label: "Companies", icon: BusinessIcon },
  { value: "directors", label: "Directors", icon: PeopleIcon },
  { value: "individuals", label: "Individuals", icon: PersonIcon },
];
const LIST_TEXT_SX = { fontSize: "0.78rem", lineHeight: 1.3, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 400 };
const LIST_SECONDARY_SX = { fontSize: "0.7rem", lineHeight: 1.2, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

function displayName(client) {
  return client?.legalName || client?.tradingName || client?.companyName || client?.name || client?.displayName || "Company";
}

function initials(name) {
  const parts = String(name || "C").trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map((part) => part[0]).join("") || "C").toUpperCase();
}

function directorLabel(director) {
  return director?.fullName || director?.name || director?.directorName || director?.displayName || director?.clientName || director?.clientId || "Director";
}

function getSelectionIdForMode(mode, item) {
  if (mode === "companies") {
    return item?.id || "";
  }
  if (mode === "directors") {
    return item?.clientId || item?.companyId || "";
  }
  return item?.id || item?._id || item?.profileId || "";
}

export default function ClientRail({ topOffset = 0, collapsed = false, pinned = false, onTogglePin, onExpand, onCollapse }) {
  const history = useHistory();
  const { pathname } = useLocation();
  const { isSuperAdmin, tenantId } = useAuthz();
  const { companies, selectedCompanyId, selectCompany } = useCompany();
  const { pinnedClientId, pinnedClientName, pinClient, readStoredTabSelection } = useClient() || {};

  const [companiesList, setCompaniesList] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(() => sessionStorage.getItem(MODE_STORAGE_KEY) || "companies");
  const [search, setSearch] = useState("");

  const activeTenantId = useMemo(() => (isSuperAdmin ? selectedCompanyId || "" : tenantId || ""), [isSuperAdmin, selectedCompanyId, tenantId]);
  const hideRail = !activeTenantId || HIDDEN_RAIL_ROUTES.has(pathname);

  useEffect(() => {
    let cancelled = false;

    async function loadEntities() {
      if (!activeTenantId || hideRail) {
        setCompaniesList([]);
        setDirectors([]);
        setIndividuals([]);
        return;
      }

      setLoading(true);
      try {
        setCompaniesList([]);
        setDirectors([]);
        setIndividuals([]);

        if (mode === "companies") {
          const { clientProfiles: list } = await listClientProfiles({ tenantId: activeTenantId, status: "active" });
          const activeCompanies = Array.isArray(list)
            ? list.filter((company) => company?.id && company?.clientType !== "individual" && !company?.linkedCompanyClientId)
            : [];
          if (!cancelled) {
            setCompaniesList(activeCompanies);
            if (activeCompanies.length > 0) {
              const rememberedSelectionId = readStoredTabSelection?.("companies") || selectedCompanyId || "";
              const rememberedSelection = activeCompanies.find((company) => getSelectionIdForMode("companies", company) === rememberedSelectionId);
              const selection = rememberedSelection || (rememberedSelectionId ? null : activeCompanies[0]) || activeCompanies[0];
              const selectionId = getSelectionIdForMode("companies", selection);
              if (selectionId) {
                selectCompany?.(selectionId);
                pinClient?.(selectionId, displayName(selection), "companies");
                history.push(`/client-profiles/${encodeURIComponent(selectionId)}`);
              }
            }
          }
        } else if (mode === "directors") {
          const { directors: list } = await listAllDirectorsForTenant(activeTenantId);
          const dirList = Array.isArray(list) ? list : [];
          if (!cancelled) {
            setDirectors(dirList);
            if (dirList.length > 0) {
              const rememberedSelectionId = readStoredTabSelection?.("directors") || pinnedClientId || "";
              const rememberedSelection = dirList.find((director) => getSelectionIdForMode("directors", director) === rememberedSelectionId);
              const selection = rememberedSelection || (rememberedSelectionId ? null : dirList[0]) || dirList[0];
              const selectionId = getSelectionIdForMode("directors", selection);
              if (selectionId) {
                pinClient?.(selectionId, directorLabel(selection), "directors");
                history.push(`/directors?clientId=${encodeURIComponent(selectionId)}`);
              }
            }
          }
        } else {
          const { clients: list } = await listIndividualClients({ tenantId: activeTenantId, status: "active" });
          const activeIndividuals = Array.isArray(list)
            ? list.filter((item) => item?.clientType === "individual" && !item?.linkedCompanyClientId)
            : [];
          if (!cancelled) {
            setIndividuals(activeIndividuals);
            if (activeIndividuals.length > 0) {
              const rememberedSelectionId = readStoredTabSelection?.("individuals") || pinnedClientId || "";
              const rememberedSelection = activeIndividuals.find((individual) => getSelectionIdForMode("individuals", individual) === rememberedSelectionId);
              const selection = rememberedSelection || (rememberedSelectionId ? null : activeIndividuals[0]) || activeIndividuals[0];
              const selectionId = getSelectionIdForMode("individuals", selection);
              if (selectionId) {
                pinClient?.(selectionId, displayName(selection), "individuals");
                history.push(`/individual-clients/${encodeURIComponent(selectionId)}`);
              }
            }
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadEntities();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId, hideRail, mode, selectedCompanyId, pinnedClientId, selectCompany, pinClient, history]);

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    sessionStorage.setItem(MODE_STORAGE_KEY, nextMode);
    setSearch("");
  };

  const handleSelectCompany = (company) => {
    const id = company?.id || "";
    if (!id) return;
    selectCompany?.(id);
    pinClient?.(id, company?.name || company?.id || "Company", "companies");
    history.push(`/client-profiles/${encodeURIComponent(id)}`);
  };

  const handleSelectDirector = (director) => {
    const clientId = director?.clientId || director?.companyId || "";
    if (clientId) pinClient?.(clientId, directorLabel(director), "directors");
    history.push(clientId ? `/directors?clientId=${encodeURIComponent(clientId)}` : "/directors");
  };

  const handleSelectIndividual = (individual) => {
    const id = individual?.id || individual?._id || individual?.profileId || "";
    if (!id) return;
    pinClient?.(id, displayName(individual), "individuals");
    history.push(`/individual-clients/${encodeURIComponent(id)}`);
  };

  if (hideRail) return null;

  const currentLabel = MODE_OPTIONS.find((option) => option.value === mode)?.label || "Companies";

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      onMouseEnter={onExpand}
      onMouseLeave={onCollapse}
      sx={{
        display: { xs: "none", md: "block" },
        width: collapsed ? 76 : 292,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: collapsed ? 76 : 292,
          boxSizing: "border-box",
          top: `${topOffset}px`,
          height: `calc(100% - ${topOffset}px)`,
          display: "flex",
          flexDirection: "column",
          background: "#ffffff",
          borderRight: `1px solid #e2e8f0`,
          overflowX: "hidden",
          boxShadow: "1px 0 4px rgba(0, 0, 0, 0.05)",
        },
      }}
    >
      <Box sx={{ px: 1.5, py: 0.5, display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-end" }}>
        <Tooltip title={pinned ? "Unpin panel" : "Pin panel open"}>
          <IconButton
            onClick={onTogglePin}
            aria-label={pinned ? "Unpin panel" : "Pin panel open"}
            size="small"
            sx={{ color: pinned ? info.main : "#475569" }}
          >
            {pinned ? <PushPinIcon aria-hidden="true" sx={{ fontSize: 18 }} /> : <PushPinOutlinedIcon aria-hidden="true" sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {!collapsed && (
        <Box sx={{ px: 1.5, py: 0.5 }}>
          <List dense disablePadding sx={{ bgcolor: "#f1f5f9", borderRadius: 2, p: 0.5, border: "1px solid #e2e8f0", display: "flex", flexDirection: "row", gap: 0.5 }}>
            {MODE_OPTIONS.map((option) => {
              const selected = mode === option.value;
              const ModeIcon = option.icon;
              return (
                <Tooltip key={option.value} title={option.label}>
                <ListItemButton
                  selected={selected}
                  onClick={() => handleModeChange(option.value)}
                  sx={{
                    flex: 1,
                    borderRadius: 1.5,
                    minHeight: 34,
                    px: 1,
                    justifyContent: "center",
                    color: selected ? "#1e293b" : "#64748b",
                    border: "1px solid transparent",
                    "&.Mui-selected": { backgroundColor: "#ffffff", border: `1px solid ${info.main}`, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
                    "&:hover": { backgroundColor: "#e2e8f0" },
                  }}
                >
                  <ModeIcon sx={{ fontSize: 16, color: selected ? info.main : "#64748b" }} />
                </ListItemButton>
                </Tooltip>
              );
            })}
          </List>
        </Box>
      )}

      {!collapsed && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${mode}…`}
            inputProps={{ "aria-label": `Search ${mode}` }}
            size="small"
            variant="outlined"
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: 32,
                color: "#1e293b",
                bgcolor: "#f8fafc",
                borderRadius: 1.5,
                fontSize: "0.78rem",
              },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
              "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2563eb" },
              "& .MuiInputBase-input::placeholder": { color: "#94a3b8", opacity: 1 },
            }}
          />
        </Box>
      )}

      <List sx={{ p: 1, pt: 0.75, borderRadius: 2, mx: 1, overflowY: "auto", overflowX: "hidden", flex: 1 }}>
        {loading && !collapsed && (
          <Box sx={{ px: 2, py: 2 }}>
            <VuiTypography variant="caption" sx={{ color: "#64748b" }}>Loading...</VuiTypography>
          </Box>
        )}

        {mode === "companies" &&
          companiesList
            .filter((company) => displayName(company).toLowerCase().includes(search.toLowerCase()))
            .map((company) => {
            const id = company?.id || "";
            const selected = id === activeTenantId || id === selectedCompanyId;
            const name = company?.legalName || company?.tradingName || company?.companyName || company?.displayName || company?.name || "Company";
            return (
              <Tooltip title={name} placement="right" arrow>
              <ListItemButton key={id} selected={selected} onClick={() => handleSelectCompany(company)} sx={{ borderRadius: 1, mb: 0.25, minHeight: 34, px: collapsed ? 1 : 1, py: 0.5, color: selected ? "#1e293b" : "#475569", border: "1px solid transparent", "&.Mui-selected": { backgroundColor: "#eff6ff", border: `1px solid ${info.main}` }, "&:hover": { backgroundColor: "#f1f5f9" } }}>
                <ListItemAvatar sx={{ minWidth: collapsed ? "auto" : 34 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: "0.72rem", fontWeight: 600, bgcolor: selected ? info.main : "#1e3a5f", color: "#fff" }}>{initials(name)}</Avatar>
                </ListItemAvatar>
                {!collapsed && <ListItemText primary={name} primaryTypographyProps={{ sx: LIST_TEXT_SX }} />}
              </ListItemButton>
              </Tooltip>
            );
          })}

        {mode === "directors" &&
          directors
            .filter((director) => directorLabel(director).toLowerCase().includes(search.toLowerCase()))
            .map((director) => {
            const clientId = director?.clientId || director?.companyId || "";
            const selected = clientId && clientId === pinnedClientId;
            const name = directorLabel(director);
            return (
              <Tooltip title={name} placement="right" arrow>
              <ListItemButton key={director?.id || clientId || name} selected={selected} onClick={() => handleSelectDirector(director)} sx={{ borderRadius: 1, mb: 0.25, minHeight: 34, px: collapsed ? 1 : 1, py: 0.5, color: selected ? "#1e293b" : "#475569", border: "1px solid transparent", "&.Mui-selected": { backgroundColor: "#eff6ff", border: `1px solid ${info.main}` }, "&:hover": { backgroundColor: "#f1f5f9" } }}>
                <ListItemAvatar sx={{ minWidth: collapsed ? "auto" : 34 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: "0.72rem", fontWeight: 600, bgcolor: selected ? info.main : "#1e3a5f", color: "#fff" }}>{initials(name)}</Avatar>
                </ListItemAvatar>
                {!collapsed && <ListItemText primary={name} secondary={director?.clientName || clientId || director?.role || ""} primaryTypographyProps={{ sx: LIST_TEXT_SX }} secondaryTypographyProps={{ sx: LIST_SECONDARY_SX }} />}
              </ListItemButton>
              </Tooltip>
            );
          })}

        {mode === "individuals" &&
          individuals
            .filter((individual) => displayName(individual).toLowerCase().includes(search.toLowerCase()))
            .map((individual) => {
            const id = individual?.id || individual?._id || individual?.profileId || "";
            const selected = id === pinnedClientId;
            const name = displayName(individual);
            return (
              <Tooltip title={name} placement="right" arrow>
              <ListItemButton key={id} selected={selected} onClick={() => handleSelectIndividual(individual)} sx={{ borderRadius: 1, mb: 0.25, minHeight: 34, px: collapsed ? 1 : 1, py: 0.5, color: selected ? "#1e293b" : "#475569", border: "1px solid transparent", "&.Mui-selected": { backgroundColor: "#eff6ff", border: `1px solid ${info.main}` }, "&:hover": { backgroundColor: "#f1f5f9" } }}>
                <ListItemAvatar sx={{ minWidth: collapsed ? "auto" : 34 }}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: "0.72rem", fontWeight: 600, bgcolor: selected ? info.main : "#1e3a5f", color: "#fff" }}>{initials(name)}</Avatar>
                </ListItemAvatar>
                {!collapsed && <ListItemText primary={name} secondary={individual?.niNumber || individual?.ni || individual?.email || ""} primaryTypographyProps={{ sx: LIST_TEXT_SX }} secondaryTypographyProps={{ sx: LIST_SECONDARY_SX }} />}
              </ListItemButton>
              </Tooltip>
            );
          })}
      </List>

    </Drawer>
  );
}

ClientRail.propTypes = {
  topOffset: PropTypes.number,
  collapsed: PropTypes.bool,
  pinned: PropTypes.bool,
  onTogglePin: PropTypes.func.isRequired,
  onExpand: PropTypes.func.isRequired,
  onCollapse: PropTypes.func.isRequired,
};

